// Shared schedule-config model + mappers used by ScheduleEditor (UI) and
// processedModelScheduleSync (data layer). Plain TS — no React imports — so utils can use it.
//
// A ScheduleConfig is the editor's working shape. It maps to/from the persisted ProcessedModelSchema
// `runSchedule` JSON ({ firstRunTime, frequency, cronExpression, timezone }) and ProcessedModelSchedule
// rows. The AI-Analyst frequency vocabulary (FrequencyType) has no "Once"/"Custom", so those live as
// editor modes here and are bridged to the runSchedule `frequency` label without touching
// scheduleFrequency.ts (which the AI-Analyst modals depend on).
import {
  FrequencyType,
  FREQ_TYPES,
  WEEK_DAYS,
  buildCronExpression,
  buildRuntime,
} from "../../utils/scheduleFrequency";

export type ScheduleMode = "recurring" | "once" | "custom";

export interface ScheduleConfig {
  // Present only for already-persisted rows (edit mode) → reconcile updates in place.
  // Absent → a new schedule, created with a fresh uuid.
  id?: string;
  mode: ScheduleMode;
  // recurring
  freqType: FrequencyType;
  hour: string; // "1".."12"
  minute: string; // "00".."59"
  ampm: "AM" | "PM";
  weekDays: string[]; // ["Mon", "Wed"] in WEEK_DAYS order, at least one (Weekly only)
  monthDay: number; // 1..31 (Monthly only)
  timezone: string;
  // once
  firstRunTime: string; // datetime-local "YYYY-MM-DDTHH:mm"
  // custom
  cronExpression: string; // raw AWS 6-field cron
}

const DEFAULT_TIMEZONE =
  (typeof Intl !== "undefined" && Intl.DateTimeFormat().resolvedOptions().timeZone) || "UTC";

export function defaultScheduleConfig(): ScheduleConfig {
  return {
    mode: "recurring",
    freqType: "Daily",
    hour: "9",
    minute: "00",
    ampm: "AM",
    weekDays: ["Mon"],
    monthDay: 1,
    timezone: DEFAULT_TIMEZONE,
    firstRunTime: "",
    cronExpression: "",
  };
}

const WEEK_CRON_TO_LABEL: Record<string, string> = {
  SUN: "Sun", MON: "Mon", TUE: "Tue", WED: "Wed", THU: "Thu", FRI: "Fri", SAT: "Sat",
};

function isFrequencyChip(value: string): value is FrequencyType {
  return (FREQ_TYPES as string[]).includes(value);
}

/** Add or remove one day from a Weekly selection, keeping WEEK_DAYS order. The last day can't be removed. */
export function toggleWeekDay(weekDays: string[], day: string): string[] {
  const next = weekDays.includes(day) ? weekDays.filter((d) => d !== day) : [...weekDays, day];
  return next.length === 0 ? weekDays : WEEK_DAYS.filter((d) => next.includes(d));
}

/** Convert an ISO/AWSDateTime to a datetime-local string in the browser's local time. */
export function isoToDatetimeLocal(iso?: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(
    d.getMinutes()
  )}`;
}

/** A datetime-local string as people read it ("Oct 1, 10:44 AM"; the year only when it isn't this one); the raw value back if it won't parse. */
export function formatDatetimeLocal(value?: string | null): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(value || "");
  if (!m) return value || "—";
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]));
  if (Number.isNaN(d.getTime())) return value || "—";
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: d.getFullYear() === new Date().getFullYear() ? undefined : "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/** Parse the time-of-day (+ day fields) out of an AWS 6-field cron into editor fields. */
function parseCronToTimeFields(cron: string): Pick<
  ScheduleConfig,
  "hour" | "minute" | "ampm" | "weekDays" | "monthDay"
> {
  const fallback = { hour: "9", minute: "00", ampm: "AM" as const, weekDays: ["Mon"], monthDay: 1 };
  if (!cron) return fallback;
  const parts = cron.trim().split(/\s+/);
  if (parts.length < 5) return fallback;
  const [min, hr, dom, , dow] = parts;
  const h24 = parseInt(hr, 10);
  const m = parseInt(min, 10);
  const ampm: "AM" | "PM" = !Number.isNaN(h24) && h24 >= 12 ? "PM" : "AM";
  const hour12 = Number.isNaN(h24) ? "9" : String(h24 % 12 === 0 ? 12 : h24 % 12);
  const minute = Number.isNaN(m) ? "00" : String(m).padStart(2, "0");
  const weekDays = parseCronDays(dow);
  const domNum = parseInt(dom, 10);
  const monthDay = Number.isNaN(domNum) ? 1 : Math.min(31, Math.max(1, domNum));
  return { hour: hour12, minute, ampm, weekDays: weekDays.length > 0 ? weekDays : ["Mon"], monthDay };
}

/** A cron day-of-week field ("MON,WED,FRI", "MON-FRI", "TUE") → editor days in WEEK_DAYS order. */
function parseCronDays(dow: string | undefined): string[] {
  const cronOrder = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
  const picked = new Set<string>();
  (dow || "").toUpperCase().split(",").forEach((part) => {
    const [from, to] = part.split("-");
    const start = cronOrder.indexOf(from);
    const end = to ? cronOrder.indexOf(to) : start;
    if (start === -1 || end === -1) return;
    for (let i = start; i <= end; i++) picked.add(WEEK_CRON_TO_LABEL[cronOrder[i]]);
  });
  return WEEK_DAYS.filter((day) => picked.has(day));
}

/**
 * Map a ScheduleConfig to the persisted runSchedule shape that `deriveScheduleTiming` consumes.
 * `frequency` carries the canonical editor label so edit mode can re-hydrate the exact chip.
 */
export function scheduleConfigToRunSchedule(cfg: ScheduleConfig): {
  firstRunTime: string;
  frequency: string;
  cronExpression: string;
  timezone: string;
} {
  const timezone = cfg.timezone || DEFAULT_TIMEZONE;
  if (cfg.mode === "once") {
    return { frequency: "ONCE", firstRunTime: cfg.firstRunTime || "", cronExpression: "", timezone };
  }
  if (cfg.mode === "custom") {
    return { frequency: "CUSTOM", firstRunTime: "", cronExpression: cfg.cronExpression || "", timezone };
  }
  // recurring — the cron carries the real cadence; frequency stores the chip name for re-hydration.
  const cronExpression = buildCronExpression(
    cfg.freqType,
    cfg.hour,
    cfg.minute,
    cfg.ampm,
    cfg.weekDays.join(","),
    cfg.monthDay
  );
  return { frequency: cfg.freqType, firstRunTime: "", cronExpression, timezone };
}

/** Build a ScheduleConfig from a persisted runSchedule JSON object (e.g. ProcessedModelSchema.runSchedule). */
export function runScheduleToScheduleConfig(runSchedule: any, id?: string): ScheduleConfig {
  const base = defaultScheduleConfig();
  const rs = typeof runSchedule === "string" ? safeParse(runSchedule) : runSchedule;
  if (!rs) return { ...base, id };

  const freq = String(rs.frequency || "ONCE");
  const up = freq.toUpperCase();
  const timezone = rs.timezone || base.timezone;

  if (up === "ONCE") {
    return { ...base, id, mode: "once", firstRunTime: isoToDatetimeLocal(rs.firstRunTime), timezone };
  }
  if (isFrequencyChip(freq) || up === "DAILY" || up === "WEEKLY" || up === "MONTHLY") {
    const freqType: FrequencyType = isFrequencyChip(freq)
      ? freq
      : up === "WEEKLY"
      ? "Weekly"
      : up === "MONTHLY"
      ? "Monthly"
      : "Daily";
    return { ...base, id, mode: "recurring", freqType, timezone, ...parseCronToTimeFields(rs.cronExpression) };
  }
  // CUSTOM / HOURLY / anything else → custom mode showing the raw cron.
  return { ...base, id, mode: "custom", cronExpression: rs.cronExpression || "", timezone };
}

/** Short human label for a schedule (used in the wizard review + Operations Log). */
export function summarizeScheduleConfig(cfg: ScheduleConfig): string {
  if (cfg.mode === "once") return `Run once at ${formatDatetimeLocal(cfg.firstRunTime)}`;
  if (cfg.mode === "custom") return `Custom cron: ${cfg.cronExpression || "—"}`;
  return buildRuntime(cfg.freqType, cfg.hour, cfg.minute, cfg.ampm, cfg.weekDays.join(","), cfg.monthDay);
}

/** Build a ScheduleConfig from a ProcessedModelSchedule row (preferred for edit — preserves row.id). */
export function scheduleRowToScheduleConfig(row: any): ScheduleConfig {
  return runScheduleToScheduleConfig(
    {
      frequency: row?.frequency,
      cronExpression: row?.cronExpression,
      firstRunTime: row?.firstRunTime,
      timezone: row?.timezone,
    },
    row?.id
  );
}

function safeParse(value: string): any {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}
